/**
* Low-level HTTP abstraction over Axios.
*
* Responsibilities:
* - Create and configure Axios instance
* - Provide common HTTP methods (get, post, patch, put, delete)
* - Deduplicate concurrent GET requests
* - Handle request cancellation by timeout
*/
import axios, {
  AxiosHeaders,
  AxiosHeaderValue,
  AxiosInstance,
  AxiosRequestConfig,
  Method,
} from "axios";
import { createApiUrl } from "@/common/utils/urls";
import sessionService from "@/common/services/session/sessionService";
import { attachCaptchaInterceptor } from "@/common/utils/attachCaptchaInterceptor";
import type { ApiClient } from "./types";

const defaultTimeout = 10000;

export const STATUS_NOT_FOUND = 404;
export const STATUS_GONE = 410;
export const STATUS_OK = 200;

export interface ErrorResponseData {
  error: { code: string; message: string; status: number };
}

export type RequestHeaders = Record<string, AxiosHeaderValue> | AxiosHeaders;

export class HttpClient implements ApiClient {
  private readonly axiosClient: AxiosInstance;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly promiseRequests: { [key: string]: Promise<any> } = {};

  public constructor (readonly baseURL?: string) {
    this.axiosClient = axios.create({
      baseURL: baseURL ?? import.meta.env.REACT_APP_API_BASE_URL ?? "/",
      responseType: "json",
      headers: {
        "Content-Type": "application/json",
      },
    });

    attachCaptchaInterceptor(this.axiosClient);
  }

  public async get<Response = unknown>(url: string, headers?: RequestHeaders): Promise<Response> {
    const keyHeaders = headers instanceof AxiosHeaders ? headers.toJSON() : headers;
    const key = JSON.stringify({
      method: "get",
      url,
      headers: keyHeaders
    });

    if (key in this.promiseRequests) {
      return this.promiseRequests[key];
    }

    this.promiseRequests[key] = this.request<Response>("get", url, undefined, headers);

    try {
      return await this.promiseRequests[key];
    } finally {
      delete this.promiseRequests[key];
    }
  }

  /**
   * Send a beacon, when available, otherwise a regular POST.
   */
  public async beacon<Response,Params>(path: string, data?: Params): Promise<Response | undefined> {
    if (navigator.sendBeacon && window.Blob) {
      const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
      const url = createApiUrl(path);

      try {
        navigator.sendBeacon(url, blob);
      } catch (e) {
        console.error(`Error sending beacon: ${e}`);
      }
    } else {
      return await this.request<Response,Params>("post", path, data);
    }
  }

  public async post<Response,Params>(url: string, data?: Params, headers?: RequestHeaders) {
    return await this.request<Response,Params>("post", url, data, headers);
  }

  public async patch<Response,Params>(url: string, data?: Params, headers?: RequestHeaders) {
    return await this.request<Response,Params>("patch", url, data, headers);
  }

  public async put<Response,Params>(url: string, data?: Params, headers?: RequestHeaders) {
    return await this.request<Response,Params>("put", url, data, headers);
  }

  public async delete<Response,Params>(url: string, data?: Params, headers?: RequestHeaders) {
    return await this.request<Response,Params>("delete", url, data, headers);
  }

  public request<Response,Params = undefined>(method: Method, url: string, data?: Params, headers?: RequestHeaders): Promise<Response> {
    // eslint-disable-next-line import-x/no-named-as-default-member
    const source = axios.CancelToken.source();
    const requestHeaders = headers ? AxiosHeaders.from(headers) : undefined;

    const config: AxiosRequestConfig<Params> = {
      method,
      url,
      data,
      headers: requestHeaders,
      cancelToken: source.token
    };

    setTimeout(() => {
      source.cancel("Internal Server Error");
    }, defaultTimeout);

    return this.axiosClient
      .request<Response>(config)
      .then(response => response.data);
  }

  public async getSessionId(): Promise<string> {
    return (await sessionService.getSession()).session;
  }

  /**
   * Our backend (Slim Framework) currently has a bug that decodes components
   * twice, so we need to encode them twice also.
   *
   * https://trello.com/c/45e8pc2a
   */
  protected encodeComponent(value: string): string {
    return encodeURIComponent(encodeURIComponent(value));
  }
}
